import React, { useEffect, useRef, useState } from "react";
import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from "@heroicons/react/24/outline";
import useToast from "./useToast";

type ImagePreviewModalProps = {
    selectedImage: string;
    onclose: () => void;
    // Optional gallery mode — when provided (2+ images), prev/next arrows
    // and a thumbnail strip let the user slide through all of them without
    // closing/reopening the modal. `selectedImage` is still required as the
    // single-image fallback for callers that don't pass a gallery.
    images?: string[];
    initialIndex?: number;
};

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
    selectedImage,
    onclose,
    images,
    initialIndex = 0,
}) => {
    const { showToast } = useToast();
    const containerRef = useRef<HTMLDivElement>(null);
    const lensRef = useRef<HTMLDivElement>(null);
    const [rotation, setRotation] = useState(0);
    const [showLens, setShowLens] = useState(false);
    const [backgroundPosition, setBackgroundPosition] = useState("0% 0%");
    const gallery = images && images.length > 0 ? images : [selectedImage];
    const [currentIndex, setCurrentIndex] = useState(Math.min(initialIndex, gallery.length - 1));
    const currentImage = gallery[currentIndex];

    const ZOOM_SCALE = 2;

    const goPrev = () => {
        setRotation(0);
        setCurrentIndex((i) => (i - 1 + gallery.length) % gallery.length);
    };
    const goNext = () => {
        setRotation(0);
        setCurrentIndex((i) => (i + 1) % gallery.length);
    };

    const rotateImage = () => setRotation((prev) => (prev + 90) % 360);

    const handleDownload = (imageUrl: string) => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // Important for CORS images
        img.src = imageUrl;

        img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            if (!ctx) return;

            // Adjust canvas size for rotation
            const angle = rotation % 360;
            const rad = (angle * Math.PI) / 180;

            const sin = Math.abs(Math.sin(rad));
            const cos = Math.abs(Math.cos(rad));
            const newWidth = img.width * cos + img.height * sin;
            const newHeight = img.width * sin + img.height * cos;

            canvas.width = newWidth;
            canvas.height = newHeight;

            // Move the origin to center of canvas before rotating
            ctx.translate(newWidth / 2, newHeight / 2);
            ctx.rotate(rad);
            ctx.drawImage(img, -img.width / 2, -img.height / 2);

            // Trigger download
            const link = document.createElement("a");
            link.download = `rotated-image-${Date.now()}.jpg`;
            link.href = canvas.toDataURL("image/jpeg");
            link.click();
        };

        img.onerror = () => {
            showToast({ severity: "error", summary: "Download Failed", detail: "Failed to load image for download. It may not support cross-origin access.", life: 3000 });
        };
    };

    // const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    //     const container = containerRef.current;
    //     if (!container) return;

    //     const rect = container.getBoundingClientRect();
    //     const centerX = rect.width / 2;
    //     const centerY = rect.height / 2;

    //     const offsetX = e.clientX - rect.left - centerX;
    //     const offsetY = e.clientY - rect.top - centerY;

    //     const angle = (-rotation * Math.PI) / 180; // rotate in reverse for pointer compensation

    //     // Rotate the mouse offset coordinates
    //     const rotatedX = offsetX * Math.cos(angle) - offsetY * Math.sin(angle);
    //     const rotatedY = offsetX * Math.sin(angle) + offsetY * Math.cos(angle);

    //     const x = centerX + rotatedX;
    //     const y = centerY + rotatedY;

    //     const percentX = (x / rect.width) * 100;
    //     const percentY = (y / rect.height) * 100;

    //     setBackgroundPosition(`${percentX}% ${percentY}%`);

    //     if (lensRef.current) {
    //         lensRef.current.style.left = `${x - 75}px`;
    //         lensRef.current.style.top = `${y - 75}px`;
    //     }
    // };

    const handlePointerMove = (clientX: number, clientY: number) => {
        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const offsetX = clientX - rect.left - centerX;
        const offsetY = clientY - rect.top - centerY;

        const angle = (-rotation * Math.PI) / 180;

        const rotatedX = offsetX * Math.cos(angle) - offsetY * Math.sin(angle);
        const rotatedY = offsetX * Math.sin(angle) + offsetY * Math.cos(angle);

        const x = centerX + rotatedX;
        const y = centerY + rotatedY;

        const percentX = (x / rect.width) * 100;
        const percentY = (y / rect.height) * 100;

        setBackgroundPosition(`${percentX}% ${percentY}%`);

        if (lensRef.current) {
            lensRef.current.style.left = `${x - 75}px`;
            lensRef.current.style.top = `${y - 75}px`;
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        handlePointerMove(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        if (!touch) return;

        handlePointerMove(touch.clientX, touch.clientY);
    };

    useEffect(() => {
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    useEffect(() => {
        if (gallery.length <= 1) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") goPrev();
            else if (e.key === "ArrowRight") goNext();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gallery.length]);

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-2 sm:px-4" >
            <div className="bg-white rounded-lg overflow-hidden w-full max-w-4xl relative my-6">

                {/* Header */}
                <div className="flex justify-between items-center px-3 py-2 border-b bg-gray-100">
                    <h2 className="text-base sm:text-lg font-semibold">
                        Image Preview{gallery.length > 1 ? ` (${currentIndex + 1}/${gallery.length})` : ""}
                    </h2>

                    <div className="flex items-center gap-3">
                        <button onClick={rotateImage} className="text-gray-500 hover:text-gray-700">
                            <ArrowPathIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>

                        <button
                            onClick={() => handleDownload(currentImage)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <ArrowDownTrayIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>

                        <button
                            onClick={() => {
                                onclose();
                                setRotation(0);
                            }}
                            className="text-gray-500 hover:text-gray-700 text-xl"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Image Container */}
                <div className="flex justify-center items-center bg-black p-3 sm:p-6 relative">
                    {gallery.length > 1 && (
                        <button
                            onClick={goPrev}
                            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 z-10"
                        >
                            <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800" />
                        </button>
                    )}
                    <div
                        ref={containerRef}
                        className="relative overflow-hidden"
                        style={{
                            width: "90vw",
                            maxWidth: "400px",
                            height: "60vh",
                            maxHeight: "400px",
                            cursor: "zoom-in",
                            backgroundImage: showLens ? `url(${currentImage})` : "none",
                            backgroundSize: showLens ? `${ZOOM_SCALE * 100}%` : "contain",
                            backgroundPosition,
                            backgroundRepeat: "no-repeat",
                            transform: `rotate(${rotation}deg)`,
                        }}
                        onMouseMove={handleMouseMove}
                        onMouseEnter={() => setShowLens(true)}
                        onMouseLeave={() => setShowLens(false)}

                        onTouchStart={() => setShowLens(true)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={() => setShowLens(false)}
                    >
                        {!showLens && (
                            <img
                                src={currentImage}
                                alt="Preview"
                                className="w-full h-full object-contain pointer-events-none"
                            />
                        )}

                        {showLens && (
                            <div
                                ref={lensRef}
                                className="absolute border-2 border-white bg-white/20 rounded"
                                style={{
                                    width: "120px",
                                    height: "120px",
                                    pointerEvents: "none",
                                }}
                            />
                        )}
                    </div>
                    {gallery.length > 1 && (
                        <button
                            onClick={goNext}
                            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 z-10"
                        >
                            <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800" />
                        </button>
                    )}
                </div>

                {gallery.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto px-3 py-2 border-t bg-gray-100">
                        {gallery.map((img, i) => (
                            <img
                                key={i}
                                src={img}
                                alt={`thumb-${i}`}
                                onClick={() => { setRotation(0); setCurrentIndex(i); }}
                                className="cursor-pointer rounded flex-shrink-0"
                                style={{
                                    width: "48px",
                                    height: "48px",
                                    objectFit: "cover",
                                    border: i === currentIndex ? "2px solid #264164" : "2px solid transparent",
                                    opacity: i === currentIndex ? 1 : 0.7,
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImagePreviewModal;
